import { useState } from "react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { COMPANY } from "@/data/content";
import { useAuth } from "@/context/AuthContext";
import { Phone, Mail, MapPin, ArrowRight, CheckCircle2, Clock, Linkedin } from "lucide-react";

export default function Contact() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    company: user?.company || "",
    email: user?.email || "",
    phone: "",
    interest: "Project Enquiry",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    if (user) {
      try {
        const all = JSON.parse(localStorage.getItem("vpcl_enquiries") || "[]");
        all.unshift({
          id: crypto.randomUUID(),
          userId: user.id,
          type: form.interest,
          subject: `${form.interest} from ${form.name}`,
          summary: form.message.slice(0, 200),
          createdAt: new Date().toISOString(),
        });
        localStorage.setItem("vpcl_enquiries", JSON.stringify(all));
      } catch { /* ignore */ }
    }
    toast.success("Thank you — your enquiry has been received.");
    setSubmitted(true);
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        overline="Contact VPCL Infra"
        title="Let's build your next renewable project."
        subtitle="Reach out to our corporate office or send a detailed project enquiry — our development desk responds within 48 hours."
        image="https://images.pexels.com/photos/5504388/pexels-photo-5504388.jpeg?auto=compress&cs=tinysrgb&w=2000"
        testid="contact-hero"
      />

      <section className="section-padding bg-white">
        <div className="container-corp grid lg:grid-cols-3 gap-px bg-[#E2E8F0]">
          {[
            { icon: MapPin, title: "Corporate Office", lines: [COMPANY.address.line1, COMPANY.address.line2, `${COMPANY.address.city} ${COMPANY.address.pin}`] },
            { icon: Phone, title: "Phone", lines: [COMPANY.phone, "Mon–Sat, 09:30–18:30 IST"] },
            { icon: Mail, title: "Email", lines: [COMPANY.email, COMPANY.landownerEmail] },
          ].map((c, i) => (
            <div key={c.title} data-testid={`contact-card-${i}`} className="bg-white p-8">
              <c.icon className="text-[#16A34A] mb-5" size={28} strokeWidth={1.5} />
              <h3 className="font-heading text-lg font-semibold text-[#0B2341] mb-4">{c.title}</h3>
              <div className="text-sm text-[#4A5568] leading-relaxed space-y-1">
                {c.lines.map((l) => <div key={l}>{l}</div>)}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#F8F9FA]">
        <div className="container-corp py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Project Enquiry</div>
            <h2 className="font-heading text-3xl lg:text-4xl font-semibold text-[#0B2341] leading-tight mb-6">
              Send us your project details.
            </h2>
            <p className="text-[#4A5568] leading-relaxed mb-8">
              Whether you represent a DISCOM, PSU, industrial offtaker, or are an investor exploring partnerships, our team is ready to engage.
            </p>
            <div className="space-y-4 text-sm">
              <div className="flex items-center gap-3 text-[#0A1118]"><Clock size={16} className="text-[#16A34A]" /> 48-hour response guarantee</div>
              <div className="flex items-center gap-3 text-[#0A1118]"><CheckCircle2 size={16} className="text-[#16A34A]" /> NDAs available on request</div>
              <div className="flex items-center gap-3 text-[#0A1118]"><Linkedin size={16} className="text-[#16A34A]" /> Connect with leadership on LinkedIn</div>
            </div>
          </div>

          <div className="lg:col-span-7 bg-white p-8 md:p-10 border border-[#E2E8F0]">
            {submitted ? (
              <div data-testid="contact-success" className="text-center py-12">
                <CheckCircle2 className="text-[#16A34A] mx-auto mb-6" size={64} strokeWidth={1.25} />
                <h3 className="font-heading text-2xl font-semibold text-[#0B2341] mb-3">Thank you, {form.name}</h3>
                <p className="text-[#4A5568] max-w-md mx-auto">
                  Your enquiry has been received. Our team will respond to{" "}
                  <span className="font-mono">{form.email}</span> within 48 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full Name *">
                    <input required value={form.name} onChange={update("name")} className={inputCls} data-testid="contact-input-name" />
                  </Field>
                  <Field label="Company / Organisation">
                    <input value={form.company} onChange={update("company")} className={inputCls} data-testid="contact-input-company" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Email *">
                    <input required type="email" value={form.email} onChange={update("email")} className={inputCls} data-testid="contact-input-email" />
                  </Field>
                  <Field label="Phone">
                    <input type="tel" value={form.phone} onChange={update("phone")} className={inputCls} data-testid="contact-input-phone" />
                  </Field>
                </div>
                <Field label="Area of Interest">
                  <select value={form.interest} onChange={update("interest")} className={inputCls} data-testid="contact-select-interest">
                    <option>Project Enquiry</option>
                    <option>Government / DISCOM Tender</option>
                    <option>EPC Partnership</option>
                    <option>Open Access / Captive Solar</option>
                    <option>Land Offer</option>
                    <option>Investor Relations</option>
                    <option>Other</option>
                  </select>
                </Field>
                <Field label="Message *">
                  <textarea required rows={5} value={form.message} onChange={update("message")} className={inputCls} data-testid="contact-input-message" placeholder="Share project scope, location, capacity, timeline..." />
                </Field>
                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  <button type="submit" className="btn-primary group flex-1" data-testid="contact-submit">
                    Submit Enquiry
                    <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button type="button" onClick={() => toast.success("Corporate Profile download will start shortly.")} className="btn-secondary flex-1" data-testid="contact-download">
                    Download Corporate Profile
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="bg-[#0B2341]">
        <div className="w-full h-[420px]" data-testid="map-container">
          <iframe
            title="VPCL Infra Office"
            src={COMPANY.mapEmbed}
            className="w-full h-full grayscale-[0.2] contrast-[1.05]"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>
    </div>
  );
}

const inputCls =
  "w-full bg-white border border-[#E2E8F0] focus:border-[#0B2341] focus:ring-1 focus:ring-[#0B2341] focus:outline-none rounded-none px-4 py-3 text-sm text-[#0A1118] placeholder:text-[#4A5568]";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[#0B2341] uppercase tracking-[0.14em] mb-2">{label}</span>
      {children}
    </label>
  );
}
